// Order status timeline component — used in TrackOrder + admin order detail
import { statusIndex } from "@/lib/seedData";
import { useLang } from "@/lib/i18n";
import { getTimelineSteps } from "@/lib/fulfillment";
import { Check } from "lucide-react";

export const OrderTimeline = ({ order }) => {
  const { isTe } = useLang();

  if (order?.status === "cancelled") {
    const cancelEvent = order?.statusHistory?.slice().reverse().find((h) => h.status === "cancelled");
    return (
      <div data-testid="order-timeline" className="flex gap-3 bg-red-50 border border-red-200 rounded-sm p-4">
        <div className="w-8 h-8 rounded-full grid place-items-center text-sm flex-shrink-0 bg-red-600 text-white">
          ✕
        </div>
        <div className="flex-1">
          <div className="text-sm font-display font-bold text-red-700">Order Cancelled</div>
          {cancelEvent?.at && (
            <div className="text-[10px] text-red-500 font-semibold mt-0.5 uppercase tracking-widest">
              {new Date(cancelEvent.at).toLocaleString()}
            </div>
          )}
          {cancelEvent?.note && (
            <div className="mt-1 text-xs bg-white border border-red-200 rounded-sm px-2.5 py-1.5 text-[#2E1A12]/80 leading-snug">
              Reason: {cancelEvent.note}
            </div>
          )}
        </div>
      </div>
    );
  }

  const currentIdx = statusIndex(order?.status || "order_created");
  const hasPayment = !!order?.paymentConfirmed;
  const steps = getTimelineSteps(order, hasPayment, isTe);

  return (
    <ol data-testid="order-timeline" className="relative space-y-2.5">
      {steps.map((s, i) => {
        const sourceIdx = statusIndex(s.key);
        const done = sourceIdx <= currentIdx;
        const active = sourceIdx === currentIdx;
        const histEvent = order?.statusHistory?.find((h) => h.status === s.key);

        return (
          <li
            key={s.key}
            data-testid={`timeline-step-${s.key}`}
            className="flex gap-3"
          >
            <div className="flex flex-col items-center">
              <div
                className={`w-8 h-8 rounded-full grid place-items-center text-sm flex-shrink-0 border-2 transition-colors ${
                  done
                    ? "bg-[#556B2F] border-[#556B2F] text-[#F5E6CC]"
                    : "bg-[#F5E6CC] border-[#5A2D1D]/30 text-[#5A2D1D]/40"
                } ${active ? "ring-2 ring-[#556B2F]/30" : ""}`}
              >
                {done ? <Check className="w-3.5 h-3.5" strokeWidth={3} /> : (
                  <span aria-hidden>{s.icon}</span>
                )}
              </div>
              {i < steps.length - 1 && (
                <div
                  className={`w-[2px] flex-1 mt-1 mb-1 ${
                    done ? "bg-[#556B2F]" : "bg-[#5A2D1D]/15"
                  }`}
                  style={{ minHeight: 16 }}
                />
              )}
            </div>
            <div className="flex-1 pb-1">
              <div
                className={`text-sm font-display font-bold ${
                  done ? "text-[#5A2D1D]" : "text-[#5A2D1D]/50"
                }`}
              >
                {isTe ? s.label_te : s.label}
              </div>
              <div className="text-xs text-[#2E1A12]/60 leading-snug">{s.desc}</div>
              {histEvent?.at && (
                <div className="text-[10px] text-[#556B2F] font-semibold mt-0.5 uppercase tracking-widest">
                  {new Date(histEvent.at).toLocaleString()}
                </div>
              )}
              {histEvent?.note && (
                <div className="mt-1 text-xs bg-[#EFD9B4]/70 border border-[#5A2D1D]/15 rounded-sm px-2.5 py-1.5 text-[#2E1A12]/80 leading-snug">
                  💬 {histEvent.note}
                </div>
              )}
              {s.key === "dispatched" && order?.courier && active && (
                <div className="mt-1 inline-block bg-[#F5E6CC] border border-[#5A2D1D]/25 px-2.5 py-1.5 text-xs rounded-sm">
                  <div><b>Courier:</b> {order.courier.name}</div>
                  <div><b>Tracking ID:</b> {order.courier.trackingId}</div>
                  {order.courier.trackingUrl && (
                    <a
                      href={order.courier.trackingUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[#556B2F] font-bold mt-0.5 inline-block hover:underline"
                    >
                      Track on courier site →
                    </a>
                  )}
                </div>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
};
